import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraLink } from '../ensigns/link';
import { SpecraStatusDot } from '../ensigns/status-dot';
import { SpecraSwatch } from '../ensigns/swatch';
import { SpecraTag } from '../ensigns/tag';
import { SpecraText } from '../ensigns/typography';
function TimelineLinks({ links, renderLink }) {
    if (!links?.length) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "mt-[var(--space-1-5)] flex flex-wrap gap-x-[var(--space-3)] gap-y-[var(--space-1)]",
        children: links.map((l)=>/*#__PURE__*/ _jsxs(SpecraLink, {
                className: "text-[length:var(--fs-ui-body)]",
                "data-item-id": l.id,
                href: l.href,
                renderLink: renderLink,
                children: [
                    l.label,
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": "true",
                        children: " →"
                    })
                ]
            }, l.id))
    });
}
/** One chronological event used by contract lineage, periods, activity, threads and version history. */ export function SpecraTimelineItem({ actions, badge, className, date, description, last = false, links, markerTone, onSelect, renderLink, selected, statusLabel, tag, title, tone = 'neutral', variant = 'rail', ...props }) {
    const lead = badge ?? (tag ? /*#__PURE__*/ _jsx(SpecraTag, {
        tone: tone,
        children: tag
    }) : null);
    if (variant === 'dated') {
        return /*#__PURE__*/ _jsxs("li", {
            className: cn('grid grid-cols-[var(--component-timeline-date-column)_minmax(0,1fr)] gap-[var(--space-3)] border-b border-border py-[var(--space-3-5)]', className),
            ...props,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    className: "pt-[var(--space-0-5)]",
                    size: "body-ui",
                    tone: "muted",
                    children: date
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-w-0",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-center gap-[var(--space-2)]",
                            children: [
                                lead,
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    className: "font-medium",
                                    size: "body-sm",
                                    children: title
                                })
                            ]
                        }),
                        description ? /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            className: "mt-[var(--space-1-5)] leading-[var(--lh-snug)]",
                            size: "body-sm",
                            tone: "secondary",
                            children: description
                        }) : null,
                        /*#__PURE__*/ _jsx(TimelineLinks, {
                            links: links,
                            renderLink: renderLink
                        }),
                        actions ? /*#__PURE__*/ _jsx("div", {
                            className: "mt-[var(--space-2)] flex flex-wrap gap-[var(--space-2)]",
                            children: actions
                        }) : null
                    ]
                })
            ]
        });
    }
    const resolvedStatusLabel = statusLabel ?? (typeof tag === 'string' ? tag : typeof title === 'string' ? title : 'Timeline event');
    const marker = onSelect ? /*#__PURE__*/ _jsx("span", {
        "aria-hidden": "true",
        className: cn('mt-[var(--space-2)] size-2.5 rounded-full border-2', selected ? 'border-foreground bg-foreground' : 'border-[color:var(--border-dashed)] bg-card')
    }) : markerTone ? /*#__PURE__*/ _jsx(SpecraSwatch, {
        className: "mt-1.5",
        label: resolvedStatusLabel,
        shape: "dot",
        tone: markerTone
    }) : /*#__PURE__*/ _jsx(SpecraStatusDot, {
        className: "mt-1.5",
        label: resolvedStatusLabel,
        tone: tone
    });
    const content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "font-medium",
                        size: "caption",
                        children: title
                    }),
                    lead,
                    date ? /*#__PURE__*/ _jsx(SpecraText, {
                        className: "ml-auto",
                        size: "micro",
                        tone: "muted",
                        children: date
                    }) : null
                ]
            }),
            description ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                className: "mt-1",
                size: "caption",
                tone: "muted",
                children: description
            }) : null
        ]
    });
    return /*#__PURE__*/ _jsxs("li", {
        className: cn('relative grid grid-cols-[var(--component-timeline-marker-column)_minmax(0,1fr)] gap-3', className),
        ...props,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center",
                children: [
                    marker,
                    !last ? /*#__PURE__*/ _jsx("span", {
                        className: "mt-1 min-h-8 w-px flex-1 bg-border"
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn('min-w-0 pb-5', last && 'pb-0'),
                children: [
                    onSelect ? /*#__PURE__*/ _jsx("button", {
                        "aria-pressed": selected ?? false,
                        className: cn('w-full rounded-[var(--radius-md)] px-[var(--space-2)] py-[var(--space-1-5)] text-left transition-colors hover:bg-[color:var(--surface-hover)]', selected && 'bg-[color:var(--surface-row-highlight)]', specraFocusRing),
                        onClick: onSelect,
                        type: "button",
                        children: content
                    }) : content,
                    /*#__PURE__*/ _jsx(TimelineLinks, {
                        links: links,
                        renderLink: renderLink
                    }),
                    actions ? /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 flex flex-wrap gap-2",
                        children: actions
                    }) : null
                ]
            })
        ]
    });
}
